import { CheckOutlined, SwapOutlined } from '@ant-design/icons';
import { useGetIdentity } from '@refinedev/core';
import { Button, Dropdown, Tooltip, type MenuProps } from 'antd';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthUser } from '../api/auth-provider.js';
import type { CrmMode } from '../api/crm-mode-config.js';
import {
  CRM_MODE_CHANGED_EVENT,
  getAvailableCrmModes,
  resolveUserCrmMode,
  setStoredCrmModeOverride,
} from '../api/crm-mode-preference.js';

function modeLabel(mode: CrmMode, isEn: boolean): string {
  if (mode === 'overseas') return isEn ? 'Overseas' : '海外';
  return isEn ? 'Domestic' : '国内';
}

/** 顶部国内/海外身份切换，仅双身份用户显示。 */
export function CrmModeSwitcher({ label }: { label?: ReactNode } = {}) {
  const { data: user } = useGetIdentity<AuthUser>();
  const { i18n } = useTranslation();
  const isEn = i18n.language?.toLowerCase().startsWith('en') ?? false;
  const availableModes = useMemo(() => getAvailableCrmModes(user), [user]);
  const [mode, setMode] = useState<CrmMode>(() => resolveUserCrmMode(user));

  useEffect(() => setMode(resolveUserCrmMode(user)), [user]);
  useEffect(() => {
    const sync = () => setMode(resolveUserCrmMode(user));
    window.addEventListener(CRM_MODE_CHANGED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CRM_MODE_CHANGED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [user]);

  if (availableModes.length < 2) return null;

  const items: MenuProps['items'] = availableModes.map((itemMode) => ({
    key: itemMode,
    icon: itemMode === mode ? <CheckOutlined /> : undefined,
    label: modeLabel(itemMode, isEn),
    onClick: () => {
      setMode(itemMode);
      setStoredCrmModeOverride(itemMode);
      void i18n.changeLanguage(itemMode === 'overseas' ? 'en' : 'zh');
    },
  }));
  const currentLabel = modeLabel(mode, isEn);

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', height: '100%', lineHeight: 1 }}>
      <Dropdown menu={{ items }} placement="bottomRight" trigger={['click']}>
        <Tooltip title={isEn ? 'Switch domestic/overseas identity' : '切换国内/海外身份'}>
          <Button
            type="text"
            aria-label={isEn ? 'Switch domestic/overseas identity' : '切换国内/海外身份'}
            className="crest-header-action"
            icon={<SwapOutlined />}
          >
            {label ?? currentLabel}
          </Button>
        </Tooltip>
      </Dropdown>
    </div>
  );
}
